import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Button,
  Grid,
  Group,
  Loader,
  Select,
  Text,
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import {
  useTripsQuery,
  useDeleteTripMutation,
} from '../../hooks/useTripsQueries';
import { useAuth } from '../../contexts/AuthContext';
import { notifications } from '@mantine/notifications';
import { ErrorDisplay } from '../common/ErrorDisplay';
import TripPagination from '../common/TripPagination';
import { openConfirmDialog } from '../common/ConfirmDialog';
import {
  DisplayableError,
  errorHandler,
  isProcessedError,
} from '../../services/utils/errorHandler';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  STATUS_LABELS,
  OCCASION_LABELS,
  OCCASIONS,
} from '../../constants';
import { ROUTES } from '../../constants/routes';
import type { Occasion, Status } from '../../types';
import { filterTrips } from '../../utils/trips/filterTrips';
import SummaryCard from './SummaryCard';
import EmptyList from './EmptyList';

/**
 * Trip list uses TanStack Query: useTripsQuery for the list (deduped by key, no Strict Mode double-fetch)
 * and useDeleteTripMutation which invalidates the list on success so data stays in sync.
 */
const TripList: React.FC = () => {
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);
  const [statusFilter, setStatusFilter] = useState<Status | 'ALL'>('ALL');
  const [occasionFilter, setOccasionFilter] = useState<Occasion | 'ALL'>('ALL');

  // Only fetch when we have a confirmed user (so invalid token doesn't trigger /trips/me 401). Same key = one request.
  const {
    data: tripsData,
    isLoading,
    error,
  } = useTripsQuery(currentPage, pageSize, {
    enabled: isAuthenticated && !!user && !authLoading,
  });
  const deleteTripMutation = useDeleteTripMutation();

  const _error: DisplayableError | null =
    error != null
      ? isProcessedError(error)
        ? error
        : error instanceof Error
          ? errorHandler.processError(error)
          : errorHandler.processError(new Error(String(error)))
      : null;

  const safeTrips = useMemo(
    () => tripsData?.content ?? [],
    [tripsData?.content]
  );

  const filteredTrips = useMemo(
    () => filterTrips(safeTrips, statusFilter, occasionFilter),
    [safeTrips, statusFilter, occasionFilter]
  );

  const filtersActive = statusFilter !== 'ALL' || occasionFilter !== 'ALL';
  const navigate = useNavigate();

  const handleDelete = (id: string | number) => {
    const idStr = String(id);
    openConfirmDialog({
      name: 'Delete Trip',
      message:
        'Are you sure you want to delete this trip? This cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteTripMutation.mutateAsync(idStr);
          notifications.show({
            title: 'Success',
            message: 'Trip deleted successfully!',
            color: 'green',
            autoClose: 1500,
          });
        } catch (_e) {
          // Error surfaced via mutation.error
        }
      },
    });
  };

  const handleEdit = (id: string | number) => {
    navigate(ROUTES.tripEdit(id));
  };

  const handleItinerary = (id: string | number) => {
    navigate(ROUTES.tripItinerary(id));
  };

  const clearFilters = () => {
    setStatusFilter('ALL');
    setOccasionFilter('ALL');
  };

  const loading = isLoading;
  if ((authLoading || loading) && safeTrips.length === 0) {
    return (
      <Container mt="xl" ta="center">
        <Loader size="lg" />
      </Container>
    );
  }

  const statusOptions = [
    { value: 'ALL', label: 'All statuses' },
    ...Object.entries(STATUS_LABELS).map(([value, label]) => ({
      value,
      label,
    })),
  ];

  const occasionOptions = [
    { value: 'ALL', label: 'All occasions' },
    ...OCCASIONS.map(id => ({
      value: id,
      label: OCCASION_LABELS[id] ?? id,
    })),
  ];

  return (
    <Container mt="xl" mb="xl" data-cy="trip-list-page">
      <Group justify="space-between" mb="lg">
        <Title order={1}>My Trips</Title>
        <Button
          variant="filled"
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate('/trips/new')}
          data-cy="new-trip"
        >
          New Trip
        </Button>
      </Group>

      {_error && <ErrorDisplay error={_error} className="mb-4" />}

      {safeTrips.length > 0 && (
        <Group mb="md" align="flex-end" wrap="wrap" justify="center">
          <Select
            label="Status"
            data={statusOptions}
            value={statusFilter}
            onChange={value =>
              setStatusFilter((value as Status | 'ALL') ?? 'ALL')
            }
            w={180}
            data-cy="trip-filter-status"
          />
          <Select
            label="Occasion"
            data={occasionOptions}
            value={occasionFilter}
            onChange={value =>
              setOccasionFilter((value as Occasion | 'ALL') ?? 'ALL')
            }
            w={200}
            data-cy="trip-filter-occasion"
          />
          {filtersActive && (
            <Group gap="sm" pb={4}>
              <Text size="sm" c="dimmed">
                {filteredTrips.length} of {safeTrips.length} trips
              </Text>
              <Button
                variant="subtle"
                size="sm"
                onClick={clearFilters}
                data-cy="trip-filter-clear"
              >
                Clear
              </Button>
            </Group>
          )}
        </Group>
      )}

      {safeTrips.length === 0 && !loading ? (
        <EmptyList />
      ) : filteredTrips.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No trips match the selected filters.
        </Text>
      ) : (
        <Grid gap="md">
          {filteredTrips.map((trip, idx) => (
            <Grid.Col span={{ base: 12, sm: 6, md: 4 }} key={trip.id ?? idx}>
              <SummaryCard
                trip={trip}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onItinerary={handleItinerary}
              />
            </Grid.Col>
          ))}
        </Grid>
      )}

      {tripsData && (
        <TripPagination
          totalPages={tripsData.totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}
    </Container>
  );
};

export default TripList;
